import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  CurrencyEur, 
  ChartLineUp, 
  Crown,
  Star,
  Lock,
  Receipt,
  CaretRight,
  MagnifyingGlass,
  Funnel,
  ArrowLeft,
  Lightning,
  TrendUp,
  UserCircle,
  CalendarBlank,
  Clock,
  CheckCircle,
  XCircle,
  Warning,
  Database,
  ArrowsClockwise,
  CloudArrowDown,
  Pulse
} from '@phosphor-icons/react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncTriggering, setSyncTriggering] = useState(false);
  
  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [userPlanFilter, setUserPlanFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');

  const checkAdminAccess = useCallback(async () => {
    try {
      const response = await api.get('/admin/check');
      if (response.data.is_admin) {
        setIsAdmin(true);
      } else {
        toast.error(t('accessDenied'));
        navigate('/dashboard');
      }
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    checkAdminAccess();
  }, [checkAdminAccess]);

  useEffect(() => {
    if (isAdmin) {
      loadAdminData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, activeTab]);

  const loadAdminData = async () => {
    try {
      if (activeTab === 'overview' || !overview) {
        const overviewRes = await api.get('/admin/overview');
        setOverview(overviewRes.data);
      }
      
      if (activeTab === 'payments') {
        const query = paymentStatusFilter ? `?status=${paymentStatusFilter}` : '';
        const paymentsRes = await api.get(`/admin/payments${query}`);
        setPayments(paymentsRes.data.payments);
      }
      
      if (activeTab === 'users') {
        let query = '?';
        if (userPlanFilter) query += `plan=${userPlanFilter}&`;
        if (userSearch) query += `search=${userSearch}&`;
        const usersRes = await api.get(`/admin/users${query}`);
        setUsers(usersRes.data.users);
      }
      
      if (activeTab === 'analytics') {
        const analyticsRes = await api.get('/admin/analytics?days=30');
        setAnalytics(analyticsRes.data);
      }
      
      if (activeTab === 'sync') {
        const syncRes = await api.get('/admin/sync/status');
        setSyncStatus(syncRes.data);
      }
    } catch {
      toast.error(t('errorLoadingData'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'vip':
        return <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-bold rounded-full">VIP</span>;
      case 'pro':
        return <span className="px-2 py-1 bg-secondary/20 text-secondary text-xs font-bold rounded-full">PRO</span>;
      default:
        return <span className="px-2 py-1 bg-white/10 text-white/50 text-xs font-bold rounded-full">FREE</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
      case 'active':
        return <span className="px-2 py-1 bg-risk-low/20 text-risk-low text-xs font-bold rounded-full flex items-center gap-1"><CheckCircle weight="fill" /> {t('active')}</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs font-bold rounded-full flex items-center gap-1"><Clock weight="fill" /> {t('pending')}</span>;
      case 'canceled':
        return <span className="px-2 py-1 bg-risk-high/20 text-risk-high text-xs font-bold rounded-full flex items-center gap-1"><XCircle weight="fill" /> {t('canceled')}</span>;
      case 'payment_failed':
        return <span className="px-2 py-1 bg-risk-high/20 text-risk-high text-xs font-bold rounded-full flex items-center gap-1"><Warning weight="fill" /> {t('failed')}</span>;
      default:
        return <span className="px-2 py-1 bg-white/10 text-white/50 text-xs font-bold rounded-full">{status || '-'}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Header */}
      <nav className="border-b border-white/5 backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="text-white/70 hover:text-white transition-colors flex items-center gap-2"
              >
                <ArrowLeft weight="bold" />
                <span>{t('dashboard')}</span>
              </button>
              <div className="h-6 w-px bg-white/10" />
              <h1 className="text-xl font-heading font-bold text-white flex items-center gap-2">
                <Crown weight="fill" className="text-primary" />
                {t('adminDashboard')}
              </h1>
            </div>
            <div className="text-white/50 text-sm">
              {user?.email}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: t('overview'), icon: ChartLineUp },
            { id: 'payments', label: t('payments'), icon: Receipt },
            { id: 'users', label: t('users'), icon: Users },
            { id: 'analytics', label: t('analytics'), icon: TrendUp },
            { id: 'sync', label: 'Data Sync', icon: Database }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 rounded-full font-bold text-sm transition-all duration-150 flex items-center gap-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary text-background'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                <Icon weight="duotone" className="text-lg" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <Users weight="duotone" className="text-2xl text-primary" />
                  <span className="text-white/50 text-sm font-bold uppercase">{t('totalUsers')}</span>
                </div>
                <div className="text-3xl font-mono font-black text-white">{overview.users.total}</div>
              </div>
              
              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 bg-risk-low rounded-full animate-pulse" />
                  <span className="text-white/50 text-sm font-bold uppercase">{t('onlineNow')}</span>
                </div>
                <div className="text-3xl font-mono font-black text-risk-low">{overview.users.online_now}</div>
              </div>
              
              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <CurrencyEur weight="duotone" className="text-2xl text-secondary" />
                  <span className="text-white/50 text-sm font-bold uppercase">{t('totalRevenue')}</span>
                </div>
                <div className="text-3xl font-mono font-black text-secondary">{formatCurrency(overview.revenue.total)}</div>
              </div>
              
              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <TrendUp weight="duotone" className="text-2xl text-primary" />
                  <span className="text-white/50 text-sm font-bold uppercase">{t('monthlyRevenue')}</span>
                </div>
                <div className="text-3xl font-mono font-black text-primary">{formatCurrency(overview.revenue.monthly)}</div>
              </div>
            </div>

            {/* User Plans Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <h3 className="text-lg font-heading font-bold text-white mb-4">{t('planDistribution')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Lock weight="fill" className="text-white/50" />
                      <span className="text-white/70">Free</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-white/30 rounded-full"
                          style={{ width: `${(overview.users.free / overview.users.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-white font-mono font-bold w-12 text-right">{overview.users.free}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Star weight="fill" className="text-secondary" />
                      <span className="text-white/70">Pro</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-secondary rounded-full"
                          style={{ width: `${(overview.users.pro / overview.users.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-secondary font-mono font-bold w-12 text-right">{overview.users.pro}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Crown weight="fill" className="text-primary" />
                      <span className="text-white/70">VIP</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${(overview.users.vip / overview.users.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-primary font-mono font-bold w-12 text-right">{overview.users.vip}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <h3 className="text-lg font-heading font-bold text-white mb-4">{t('subscriptionStatus')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">{t('active')}</span>
                    <span className="text-risk-low font-mono font-bold">{overview.subscriptions.active}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">{t('canceled')}</span>
                    <span className="text-risk-high font-mono font-bold">{overview.subscriptions.canceled}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">{t('expired')}</span>
                    <span className="text-white/50 font-mono font-bold">{overview.subscriptions.expired}</span>
                  </div>
                </div>
                <div className="border-t border-white/10 mt-4 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">{t('sharpslipsToday')}</span>
                    <span className="text-primary font-mono font-bold">{overview.sharpslips.today}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-white/70">{t('sharpslipsTotal')}</span>
                    <span className="text-white font-mono font-bold">{overview.sharpslips.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <select
                value={paymentStatusFilter}
                onChange={(e) => { setPaymentStatusFilter(e.target.value); loadData(); }}
                className="bg-white/5 border border-white/10 rounded-sm px-4 py-2 text-white text-sm focus:outline-none focus:border-primary"
              >
                <option value="">{t('allStates')}</option>
                <option value="paid">{t('paid')}</option>
                <option value="pending">{t('pending')}</option>
                <option value="failed">{t('failed')}</option>
              </select>
            </div>

            {/* Payments Table */}
            <div className="glass-surface rounded-sm border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('email')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('plan')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('amount')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('status')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-white/50">{t('noPayments')}</td>
                    </tr>
                  ) : (
                    payments.map((payment, idx) => (
                      <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                        <td className="py-3 px-4 text-white text-sm">{payment.email}</td>
                        <td className="py-3 px-4">{getPlanBadge(payment.plan)}</td>
                        <td className="py-3 px-4 text-secondary font-mono font-bold">{formatCurrency(payment.amount)}</td>
                        <td className="py-3 px-4">{getStatusBadge(payment.payment_status)}</td>
                        <td className="py-3 px-4 text-white/50 text-sm">{formatDate(payment.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1 max-w-xs">
                <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
                <input
                  type="text"
                  placeholder={t('searchEmailName')}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadData()}
                  className="w-full bg-white/5 border border-white/10 rounded-sm pl-10 pr-4 py-2 text-white text-sm focus:outline-none focus:border-primary"
                />
              </div>
              <select
                value={userPlanFilter}
                onChange={(e) => { setUserPlanFilter(e.target.value); loadData(); }}
                className="bg-white/5 border border-white/10 rounded-sm px-4 py-2 text-white text-sm focus:outline-none focus:border-primary"
              >
                <option value="">{t('allPlans')}</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="vip">VIP</option>
              </select>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-primary text-background font-bold text-sm rounded-sm"
              >
                {t('search')}
              </button>
            </div>

            {/* Users Table */}
            <div className="glass-surface rounded-sm border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('user')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('plan')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('status')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('sharpslips')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('registration')}</th>
                    <th className="text-left py-3 px-4 text-white/50 text-xs font-bold uppercase">{t('lastLogin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-white/50">{t('noUsers')}</td>
                    </tr>
                  ) : (
                    users.map((u, idx) => (
                      <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                              ) : (
                                <UserCircle weight="fill" className="text-white/50 text-xl" />
                              )}
                            </div>
                            <div>
                              <div className="text-white text-sm font-semibold">{u.name}</div>
                              <div className="text-white/50 text-xs">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">{getPlanBadge(u.plan)}</td>
                        <td className="py-3 px-4">{getStatusBadge(u.subscription_status)}</td>
                        <td className="py-3 px-4 text-white font-mono">{u.total_sharpslips_generated || 0}</td>
                        <td className="py-3 px-4 text-white/50 text-sm">{formatDate(u.created_at)}</td>
                        <td className="py-3 px-4 text-white/50 text-sm">{formatDate(u.last_login)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-8">
            {/* Conversion Rates */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-surface p-6 rounded-sm border border-secondary/30">
                <h3 className="text-lg font-heading font-bold text-white mb-2">{t('freeToProConversion')}</h3>
                <div className="text-4xl font-mono font-black text-secondary">{analytics.conversions.free_to_pro_percent}%</div>
                <p className="text-white/50 text-sm mt-2">{t('conversionRate')}</p>
              </div>
              <div className="glass-surface p-6 rounded-sm border border-primary/30">
                <h3 className="text-lg font-heading font-bold text-white mb-2">{t('proToVipConversion')}</h3>
                <div className="text-4xl font-mono font-black text-primary">{analytics.conversions.pro_to_vip_percent}%</div>
                <p className="text-white/50 text-sm mt-2">{t('upgradeRate')}</p>
              </div>
            </div>

            {/* Most Active Users */}
            <div className="glass-surface p-6 rounded-sm border border-white/10">
              <h3 className="text-lg font-heading font-bold text-white mb-4">{t('mostActiveUsers')}</h3>
              <div className="space-y-3">
                {analytics.most_active_users.length === 0 ? (
                  <p className="text-white/50">{t('noActivityData')}</p>
                ) : (
                  analytics.most_active_users.map((u, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="text-white/30 font-mono text-sm w-6">{idx + 1}.</span>
                        <div>
                          <div className="text-white text-sm font-semibold">{u.name || u.email}</div>
                          <div className="text-white/50 text-xs">{u.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getPlanBadge(u.plan)}
                        <div className="text-primary font-mono font-bold">{u.total_sharpslips_generated} slips</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Daily Activity Charts (simplified) */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <h3 className="text-lg font-heading font-bold text-white mb-4">{t('slipsPerDay')}</h3>
                <div className="flex items-end gap-2 h-32">
                  {analytics.sharpslips_per_day.map((day, idx) => {
                    const maxCount = Math.max(...analytics.sharpslips_per_day.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-primary/50 rounded-t transition-all duration-300"
                          style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                        />
                        <span className="text-white/30 text-xs">{day.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="glass-surface p-6 rounded-sm border border-white/10">
                <h3 className="text-lg font-heading font-bold text-white mb-4">{t('signupsPerDay')}</h3>
                <div className="flex items-end gap-2 h-32">
                  {analytics.signups_per_day.map((day, idx) => {
                    const maxCount = Math.max(...analytics.signups_per_day.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-secondary/50 rounded-t transition-all duration-300"
                          style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                        />
                        <span className="text-white/30 text-xs">{day.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sync Tab */}
        {activeTab === 'sync' && (
          <div className="space-y-8">
            {/* Sync Controls */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold text-white flex items-center gap-3">
                <Database weight="duotone" className="text-primary" />
                Data Sync Pipeline
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    setSyncTriggering(true);
                    try {
                      await api.post('/admin/sync/trigger?sync_type=fixtures');
                      toast.success('Fixtures sync triggered!');
                      const res = await api.get('/admin/sync/status');
                      setSyncStatus(res.data);
                    } catch (e) {
                      toast.error('Sync failed');
                    } finally {
                      setSyncTriggering(false);
                    }
                  }}
                  disabled={syncTriggering}
                  className="px-4 py-2 bg-secondary/20 text-secondary hover:bg-secondary/30 rounded-sm font-bold text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <CloudArrowDown weight="bold" />
                  Sync Fixtures
                </button>
                <button
                  onClick={async () => {
                    setSyncTriggering(true);
                    try {
                      await api.post('/admin/sync/trigger?sync_type=odds');
                      toast.success('Odds sync triggered!');
                      const res = await api.get('/admin/sync/status');
                      setSyncStatus(res.data);
                    } catch (e) {
                      toast.error('Sync failed');
                    } finally {
                      setSyncTriggering(false);
                    }
                  }}
                  disabled={syncTriggering}
                  className="px-4 py-2 bg-primary/20 text-primary hover:bg-primary/30 rounded-sm font-bold text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <ArrowsClockwise weight="bold" className={syncTriggering ? 'animate-spin' : ''} />
                  Sync Odds
                </button>
                <button
                  onClick={async () => {
                    const res = await api.get('/admin/sync/status');
                    setSyncStatus(res.data);
                    toast.success('Status refreshed');
                  }}
                  className="px-4 py-2 bg-white/5 text-white/70 hover:bg-white/10 rounded-sm font-bold text-sm transition-colors flex items-center gap-2"
                >
                  <ArrowsClockwise weight="bold" />
                  Refresh
                </button>
              </div>
            </div>

            {syncStatus && (
              <>
                {/* Status Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="glass-surface p-6 rounded-sm border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <Pulse weight="duotone" className="text-2xl text-primary" />
                      <span className="text-white/50 text-sm font-bold uppercase">Scheduler</span>
                    </div>
                    <div className={`text-xl font-mono font-black flex items-center gap-2 ${syncStatus.scheduler?.running ? 'text-risk-low' : 'text-risk-high'}`}>
                      {syncStatus.scheduler?.running ? (
                        <>
                          <CheckCircle weight="fill" />
                          Running
                        </>
                      ) : (
                        <>
                          <XCircle weight="fill" />
                          Stopped
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="glass-surface p-6 rounded-sm border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <Database weight="duotone" className="text-2xl text-secondary" />
                      <span className="text-white/50 text-sm font-bold uppercase">Total Fixtures</span>
                    </div>
                    <div className="text-3xl font-mono font-black text-secondary">
                      {syncStatus.database?.total_fixtures || 0}
                    </div>
                    <div className="text-white/40 text-xs mt-1">
                      {syncStatus.database?.upcoming_fixtures || 0} upcoming
                    </div>
                  </div>
                  
                  <div className="glass-surface p-6 rounded-sm border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <Lightning weight="duotone" className="text-2xl text-primary" />
                      <span className="text-white/50 text-sm font-bold uppercase">With Odds</span>
                    </div>
                    <div className="text-3xl font-mono font-black text-primary">
                      {syncStatus.database?.fixtures_with_odds || 0}
                    </div>
                  </div>
                  
                  <div className="glass-surface p-6 rounded-sm border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendUp weight="duotone" className="text-2xl text-risk-low" />
                      <span className="text-white/50 text-sm font-bold uppercase">API Calls Today</span>
                    </div>
                    <div className="text-3xl font-mono font-black text-risk-low">
                      {syncStatus.api_usage?.requests || 0}
                    </div>
                  </div>
                </div>

                {/* Sync Times */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="glass-surface p-6 rounded-sm border border-white/10">
                    <h3 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2">
                      <Clock weight="duotone" className="text-primary" />
                      Last Sync
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-white/60">Fixtures</span>
                        <span className="text-white font-mono text-sm">
                          {syncStatus.last_sync?.fixtures 
                            ? new Date(syncStatus.last_sync.fixtures).toLocaleString()
                            : 'Never'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-white/60">Odds</span>
                        <span className="text-white font-mono text-sm">
                          {syncStatus.last_sync?.odds 
                            ? new Date(syncStatus.last_sync.odds).toLocaleString()
                            : 'Never'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-white/60">Cleanup</span>
                        <span className="text-white font-mono text-sm">
                          {syncStatus.last_sync?.cleanup 
                            ? new Date(syncStatus.last_sync.cleanup).toLocaleString()
                            : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="glass-surface p-6 rounded-sm border border-white/10">
                    <h3 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2">
                      <CalendarBlank weight="duotone" className="text-secondary" />
                      Next Sync
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-white/60">Fixtures (every {syncStatus.scheduler?.fixtures_interval_hours || 4}h)</span>
                        <span className="text-white font-mono text-sm">
                          {syncStatus.next_sync?.fixtures 
                            ? (syncStatus.next_sync.fixtures === 'Now' ? 'Now' : new Date(syncStatus.next_sync.fixtures).toLocaleString())
                            : 'Unknown'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-white/60">Odds (every {syncStatus.scheduler?.odds_interval_minutes || 30}m)</span>
                        <span className="text-white font-mono text-sm">
                          {syncStatus.next_sync?.odds 
                            ? (syncStatus.next_sync.odds === 'Now' ? 'Now' : new Date(syncStatus.next_sync.odds).toLocaleString())
                            : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sync Logs */}
                <div className="glass-surface p-6 rounded-sm border border-white/10">
                  <h3 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2">
                    <Receipt weight="duotone" className="text-primary" />
                    Recent Sync Logs
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {syncStatus.recent_logs?.length > 0 ? (
                      syncStatus.recent_logs.map((log, idx) => (
                        <div key={idx} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                            log.status === 'completed' ? 'bg-risk-low/20 text-risk-low' :
                            log.status === 'failed' ? 'bg-risk-high/20 text-risk-high' :
                            log.status === 'started' ? 'bg-secondary/20 text-secondary' :
                            'bg-white/10 text-white/50'
                          }`}>
                            {log.status}
                          </span>
                          <span className="text-white/80 text-sm flex-1">[{log.type}] {log.details}</span>
                          <span className="text-white/40 text-xs font-mono">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-white/40 text-center py-4">No logs available</div>
                    )}
                  </div>
                </div>
              </>
            )}

            {!syncStatus && (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white/50">Loading sync status...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
